<!-- Poppin'Text - Makes your text"kirakira dokidoki"!! -->

<script setup lang="ts">
import type { Animator } from './animators'

import { readGraphemeClusters } from 'clustr'
import { onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'

const props = defineProps<{
  /**
   * A plain string or a ReadableStream of bytes from text in UTF-8 encoding.
   * If a stream is provided, the stream **SHOULD NOT** be reused. (i.e. You should not set a same stream twice.)
   */
  text?: string | ReadableStream<Uint8Array>
  textClass?: string | string[]
  animator?: Animator
}>()

const emits = defineEmits<{
  (e: 'textSplit', grapheme: string): void
}>()

const targets = ref<string[]>([])
const abortController = shallowRef<AbortController>()
const activeReader = shallowRef<ReadableStreamDefaultReader<Uint8Array>>()
const segmenter = new Intl.Segmenter('und', { granularity: 'grapheme' })

// 流式文本不必每个字都触发一次 Vue 更新：把同一帧到达的字符合并，
// 既保留逐字显示效果，也避免长回复期间创建大量微小的渲染任务。
const pendingClusters: string[] = []
let flushHandle: number | null = null
let flushHandleIsRaf = false

function flushPendingClusters() {
  flushHandle = null
  if (pendingClusters.length === 0)
    return

  targets.value.push(...pendingClusters.splice(0))
}

function scheduleFlush() {
  if (flushHandle !== null)
    return

  if (typeof requestAnimationFrame === 'function') {
    flushHandleIsRaf = true
    flushHandle = requestAnimationFrame(flushPendingClusters)
  }
  else {
    flushHandleIsRaf = false
    flushHandle = window.setTimeout(flushPendingClusters, 16)
  }
}

function cancelScheduledFlush() {
  if (flushHandle === null)
    return

  if (flushHandleIsRaf)
    cancelAnimationFrame(flushHandle)
  else
    clearTimeout(flushHandle)
  flushHandle = null
}

function flushNow() {
  cancelScheduledFlush()
  flushPendingClusters()
}

let streamGeneration = 0

watch(() => props.text, async (text) => {
  const generation = ++streamGeneration
  flushNow()
  pendingClusters.length = 0
  abortController.value?.abort()
  activeReader.value?.cancel().catch(() => {})
  activeReader.value = undefined

  if (!text)
    return
  if (typeof text === 'string') {
    targets.value = [...segmenter.segment(text)].map(seg => seg.segment)
  }
  else {
    abortController.value = new AbortController()
    try {
      targets.value = []
      if (typeof text?.getReader !== 'function') {
        console.warn('⚠️ PoppinText: text 不是有效的 ReadableStream，降级为字符串处理')
        targets.value = [...segmenter.segment(String(text))].map(seg => seg.segment)
        return
      }
      // 持有 reader 引用，组件卸载时 cancel 它以触发底层 stream 的 cancel 钩子，
      // 停止 createTextStream 的后台逐字空转（切换会话/删除消息时不再空跑定时器）
      const reader = text.getReader()
      activeReader.value = reader
      for await (const cluster of readGraphemeClusters(reader, { signal: abortController.value.signal })) {
        if (generation !== streamGeneration)
          return

        pendingClusters.push(cluster)
        scheduleFlush()
        emits('textSplit', cluster)
      }
      if (generation === streamGeneration)
        flushNow()
    }
    catch (error) {
      if (error instanceof Error && (error.message === 'Aborted' || error.message === 'Operation canceled' || error.message === 'Operation cancelled' || error.name === 'AbortError')) {
        // 静默处理：用户中断文本流是正常操作
      }
      else {
        console.error('Error reading text:', error)
      }
    }
  }
}, { immediate: true })

onUnmounted(() => {
  streamGeneration++
  flushNow()
  abortController.value?.abort()
  // cancel 底层 stream，触发 createTextStream 的 cancel 钩子停止后台空转
  activeReader.value?.cancel().catch(() => {})
})

const elements = ref<HTMLElement[]>([])
const animatorCleanupFn = shallowRef<() => void>()

onMounted(() => {
  animatorCleanupFn.value = props.animator?.(elements.value)
})

const lastAnimatedIndex = ref(-1)

watch([targets, () => props.animator], ([targets, animator]) => {
  if (typeof props.text === 'string') {
    animatorCleanupFn.value?.()
    animatorCleanupFn.value = animator?.(elements.value)
  }
  else {
    animator?.(elements.value.slice(lastAnimatedIndex.value, targets.length))
    lastAnimatedIndex.value = targets.length
  }
}, { deep: true, flush: 'post' }) // <- Ensure post-update refs
</script>

<template>
  <div>
    <span
      v-for="(grapheme, index) in targets"
      :key="index"
      ref="elements"
      style="color: inherit"
      :class="[
        // 仅在有动画器时用 inline-block（transform/opacity 动画需要）；
        // 无动画器（如线下模式逐字流式）时用 inline，避免 inline-block 文字退化成灰阶抗锯齿而发白
        props.animator ? 'inline-block' : 'inline',
        ...(typeof props.textClass === 'string' ? [props.textClass] : (props.textClass || [])),
      ]"
    >
      {{ grapheme }}
    </span>
  </div>
</template>
